import React, { useState, useEffect, useRef } from 'react';
import { useB2bRates } from '../../../hooks/useB2bRates';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { useModal } from '../../../hooks/useModal';
import toast from 'react-hot-toast';
import { Download, Upload, Save, Pencil, X, Loader2, Plus, Trash2 } from 'lucide-react';

export default function B2bRatesPage({ hidePadding = false }: { hidePadding?: boolean }) {
  const {
    useB2bPlansQuery,
    useB2bRatesQuery,
    useUpdateB2bRatesMutation,
    useAddChargeMutation,
    useUpdateChargeMutation,
    useDeleteChargeMutation,
    useUploadB2bRatesMutation,
    useUploadNewB2bPlanMutation,
    downloadB2bRates,
    downloadSampleB2bRates,
  } = useB2bRates();

  const { data: plansData, isLoading: isLoadingPlans } = useB2bPlansQuery();
  const rawPlans = (Array.isArray(plansData) ? plansData : (plansData as any)?.data) || [];
  const plans = React.useMemo(() => {
    return rawPlans.filter((p: any) => {
      const type = (p.type || '').toLowerCase();
      const name = (p.name || '').toLowerCase();
      return type === 'b2b' || name.startsWith('b2b_');
    });
  }, [rawPlans]);

  const [selectedPlanId, setSelectedPlanId] = useState<string>('');

  const { data: ratesData, isLoading: isLoadingRates } = useB2bRatesQuery(selectedPlanId, { enabled: !!selectedPlanId });
  const couriers = ratesData?.couriers || [];

  const [activeTab, setActiveTab] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { open, close, Modal } = useModal();

  const { mutate: updateRates, isPending: isUpdating } = useUpdateB2bRatesMutation();
  const { mutate: addCharge, isPending: isAddingCharge } = useAddChargeMutation();
  const { mutate: updateCharge, isPending: isUpdatingCharge } = useUpdateChargeMutation();
  const { mutate: deleteCharge, isPending: isDeletingCharge } = useDeleteChargeMutation();
  const { mutate: uploadRates, isPending: isUploading } = useUploadB2bRatesMutation();

  const [editedMatrix, setEditedMatrix] = useState<any[]>([]);
  const [editedCharges, setEditedCharges] = useState<any[]>([]);
  const [newCharge, setNewCharge] = useState({ code: '', label: '', type: 'FIXED', value: '', min: '', unit: '' });
  const [showAddCharge, setShowAddCharge] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: uploadNewPlanRates, isPending: isUploadingNew } = useUploadNewB2bPlanMutation();
  const [isDownloadingSample, setIsDownloadingSample] = useState(false);

  const newPlanFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (couriers.length > 0 && activeTab === null) {
      setActiveTab(couriers[0].courierId);
    }
  }, [couriers, activeTab]);

  useEffect(() => {
    if (activeTab !== null) {
      const courier = couriers.find((c: any) => c.courierId === activeTab);
      if (courier) {
        setEditedMatrix(JSON.parse(JSON.stringify(courier.matrix || [])));
        setEditedCharges(JSON.parse(JSON.stringify(courier.charges || [])));
      }
    }
  }, [activeTab, couriers, isEditing]);

  const activeCourier = couriers.find((c: any) => c.courierId === activeTab);

  const handleMatrixChange = (index: number, value: string) => {
    if (value && !/^\d*\.?\d{0,2}$/.test(value)) return;
    const newData = [...editedMatrix];
    newData[index].ratePerKg = value === '' ? 0 : parseFloat(value);
    setEditedMatrix(newData);
  };

  const handleChargeChange = (index: number, field: string, value: string) => {
    if (field === 'chargeValue' || field === 'minValue') {
      if (value && !/^\d*\.?\d{0,2}$/.test(value)) return;
      const newData = [...editedCharges];
      newData[index][field] = value === '' ? null : parseFloat(value);
      setEditedCharges(newData);
    } else {
      const newData = [...editedCharges];
      newData[index][field] = value;
      setEditedCharges(newData);
    }
  };

  const handleSave = () => {
    if (!selectedPlanId || !activeTab) return;
    
    updateRates({
      planId: selectedPlanId,
      courierId: activeTab,
      payload: { matrix: editedMatrix, charges: editedCharges }
    }, {
      onSuccess: () => {
        toast.success('Rates updated successfully');
        setIsEditing(false);
      },
      onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to update rates')
    });
  };

  const handleAddChargeSubmit = () => {
    if (!selectedPlanId || !activeTab || !newCharge.label || !newCharge.value) {
      toast.error('Please fill required fields');
      return;
    }

    const newChargeObj = {
      chargeCode: newCharge.code || `CUST_${Date.now()}`,
      chargeLabel: newCharge.label,
      chargeType: newCharge.type,
      chargeValue: parseFloat(newCharge.value),
      minValue: newCharge.min ? parseFloat(newCharge.min) : null,
      unit: newCharge.unit || null,
    };
    const updatedCharges = [...editedCharges, newChargeObj];

    updateRates({
      planId: selectedPlanId,
      courierId: activeTab,
      payload: {
        matrix: editedMatrix,
        charges: updatedCharges,
      }
    }, {
      onSuccess: () => {
        setEditedCharges(updatedCharges);
        toast.success('Charge added');
        setShowAddCharge(false);
        setNewCharge({ code: '', label: '', type: 'FIXED', value: '', min: '', unit: '' });
      },
      onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to add charge')
    });
  };

  const handleDeleteCharge = (chargeIndex: number) => {
    if (!selectedPlanId || !activeTab) return;
    if (window.confirm('Are you sure you want to remove this charge?')) {
      const updatedCharges = editedCharges.filter((_, idx) => idx !== chargeIndex);
      updateRates({
        planId: selectedPlanId,
        courierId: activeTab,
        payload: {
          matrix: editedMatrix,
          charges: updatedCharges,
        }
      }, {
        onSuccess: () => {
          setEditedCharges(updatedCharges);
          toast.success('Charge removed');
        },
        onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to remove charge')
      });
    }
  };

  const handleEditChargeClick = (charge: any, chargeIndex: number) => {
    open({
      Component: () => {
        const [label, setLabel] = useState(charge.chargeLabel || '');
        const [code, setCode] = useState(charge.chargeCode || '');
        const [type, setType] = useState(charge.chargeType || 'FIXED');
        const [value, setValue] = useState(charge.chargeValue !== null && charge.chargeValue !== undefined ? String(charge.chargeValue) : '');
        const [min, setMin] = useState(charge.minValue !== null && charge.minValue !== undefined ? String(charge.minValue) : '');
        const [unit, setUnit] = useState(charge.unit || '');

        return (
          <div className="bg-white p-6 rounded-xl w-96 max-w-full font-sans">
            <h2 className="text-xl font-bold mb-1 text-gray-900">Edit Additional Charge</h2>
            <p className="text-gray-500 text-sm mb-4">Update surcharge details for this courier.</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Charge Label *</label>
                <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Fuel Surcharge" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Code</label>
                  <Input value={code} onChange={e => setCode(e.target.value)} placeholder="e.g. FSC" className="font-mono text-xs" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Type *</label>
                  <Select
                    value={type}
                    onChange={e => setType(e.target.value)}
                    options={[
                      { label: 'FIXED', value: 'FIXED' },
                      { label: 'PERCENTAGE', value: 'PERCENTAGE' },
                    ]}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Value *</label>
                  <Input type="number" value={value} onChange={e => setValue(e.target.value)} placeholder="0.00" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Min Value</label>
                  <Input type="number" value={min} onChange={e => setMin(e.target.value)} placeholder="0.00" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Unit</label>
                <Input value={unit} onChange={e => setUnit(e.target.value)} placeholder="e.g. per kg, per shipment" />
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button
                className="bg-[#008080] hover:bg-teal-700 text-white"
                disabled={!label.trim() || !value.trim() || isUpdating}
                onClick={() => {
                  if (!selectedPlanId || !activeTab) return;
                  const updatedCharges = editedCharges.map((ch, idx) => {
                    if (idx === chargeIndex) {
                      return {
                        ...ch,
                        chargeCode: code || ch.chargeCode,
                        chargeLabel: label,
                        chargeType: type,
                        chargeValue: parseFloat(value),
                        minValue: min ? parseFloat(min) : null,
                        unit: unit || null,
                      };
                    }
                    return ch;
                  });

                  updateRates({
                    planId: selectedPlanId,
                    courierId: activeTab,
                    payload: {
                      matrix: editedMatrix,
                      charges: updatedCharges,
                    }
                  }, {
                    onSuccess: () => {
                      setEditedCharges(updatedCharges);
                      toast.success('Charge updated successfully');
                      close();
                    },
                    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to update charge')
                  });
                }}
              >
                {isUpdating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Changes
              </Button>
            </div>
          </div>
        );
      }
    });
  };

  const handleDownload = async () => {
    if (!selectedPlanId) return;
    try {
      setIsDownloading(true);
      const blob = await downloadB2bRates(selectedPlanId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rates_sheet_plan_${selectedPlanId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download rates');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadSample = async () => {
    try {
      setIsDownloadingSample(true);
      await downloadSampleB2bRates();
    } catch (error) {
      toast.error('Failed to download sample rates');
    } finally {
      setIsDownloadingSample(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedPlanId) return;

    if (!file.name.endsWith('.xlsx')) {
      toast.error('Please upload a valid Excel (.xlsx) file');
      return;
    }

    uploadRates({ planId: selectedPlanId, file }, {
      onSuccess: () => {
        toast.success('Rates uploaded successfully');
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
      onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to upload rates')
    });
  };

  const handleNewPlanFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx')) {
      toast.error('Please upload a valid Excel (.xlsx) file');
      return;
    }

    open({
      Component: () => {
        const [planName, setPlanName] = useState('');
        return (
          <div className="bg-white p-6 rounded-xl w-96 font-sans">
            <h2 className="text-xl font-bold mb-4">Create New Plan</h2>
            <p className="text-gray-500 mb-4 text-sm">Enter a name for the new B2B plan to be created from this sheet.</p>
            <Input 
              placeholder="e.g. standard_b2b_2024" 
              value={planName} 
              onChange={e => setPlanName(e.target.value)} 
              className="mb-6"
              autoFocus
            />
            <div className="flex gap-4">
              <Button variant="outline" className="flex-1" onClick={() => {
                if (newPlanFileInputRef.current) newPlanFileInputRef.current.value = '';
                close();
              }}>Cancel</Button>
              <Button
                className="flex-1 bg-[#008080] hover:bg-teal-700 text-white"
                disabled={!planName.trim() || isUploadingNew}
                onClick={() => {
                  uploadNewPlanRates({ planName, file }, {
                    onSuccess: (data: any) => {
                      toast.success('New plan uploaded and created successfully');
                      setSelectedPlanId(data.id.toString());
                      if (newPlanFileInputRef.current) newPlanFileInputRef.current.value = '';
                      close();
                    },
                    onError: (err: any) => {
                      toast.error(err.response?.data?.message || 'Failed to upload new plan');
                      if (newPlanFileInputRef.current) newPlanFileInputRef.current.value = '';
                      close();
                    }
                  });
                }}
              >
                {isUploadingNew ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Upload & Create
              </Button>
            </div>
          </div>
        );
      }
    });
  };



  // Build matrix grid data for UI
  const originZones = Array.from(new Set(editedMatrix.map(m => m.originZone))).sort();
  const destZones = Array.from(new Set(editedMatrix.map(m => m.destinationZone))).sort();

  return (
    <div className={hidePadding ? "bg-gray-50 min-h-screen" : "p-6 bg-gray-50 min-h-screen"}>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">B2B Rate Card Management</h1>
          <p className="text-gray-500 mt-1">Manage courier matrices and additional charges for B2B plans.</p>
        </div>
        
        <div className="flex items-center gap-4">
          <Button 
            onClick={handleDownloadSample} 
            disabled={isDownloadingSample}
            variant="outline"
            className="flex items-center gap-2"
          >
            {isDownloadingSample ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            B2B Sample Sheet
          </Button>

          <Button 
            onClick={() => newPlanFileInputRef.current?.click()}
            disabled={isUploadingNew}
            variant="outline"
            className="flex items-center gap-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50"
          >
            {isUploadingNew ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload as New Plan
          </Button>
          <input 
            type="file" 
            ref={newPlanFileInputRef} 
            className="hidden" 
            accept=".xlsx"
            onChange={handleNewPlanFileUpload} 
          />



          <Select
            value={selectedPlanId}
            onChange={(e) => setSelectedPlanId(e.target.value)}
            disabled={isLoadingPlans}
            className="w-64"
            options={[
              { label: 'Select Plan...', value: '' },
              ...plans.map((p: any) => ({ label: p.name, value: p.id.toString() }))
            ]}
          />

          <Button 
            onClick={handleDownload} 
            disabled={!selectedPlanId || isDownloading}
            variant="outline"
            className="flex items-center gap-2"
          >
            {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Rates Sheet
          </Button>
          
          <Button 
            onClick={() => fileInputRef.current?.click()} 
            disabled={!selectedPlanId || isUploading}
            variant="outline"
            className="flex items-center gap-2"
          >
            {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload to Plan
          </Button>
          <input 
            type="file" 
            ref={fileInputRef} 
            className="hidden" 
            accept=".xlsx"
            onChange={handleFileUpload} 
          />
        </div>
      </div>

      {!selectedPlanId ? (
        <div className="bg-white p-12 text-center rounded-xl border border-gray-200 shadow-sm">
          <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Pencil className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900">No Plan Selected</h3>
          <p className="text-gray-500 mt-2 mb-6">Please select a B2B plan from the dropdown above, or upload a sheet directly to get started.</p>
          
          <div className="flex justify-center gap-4">

            <Button onClick={() => newPlanFileInputRef.current?.click()} variant="outline" className="flex items-center gap-2 border-indigo-600 text-indigo-600">
              <Upload className="w-4 h-4" /> Upload Sheet as New Plan
            </Button>
            <Button onClick={handleDownloadSample} variant="outline" className="flex items-center gap-2">
              <Download className="w-4 h-4" /> Download Sample Sheet
            </Button>
          </div>
        </div>
      ) : isLoadingRates ? (
        <div className="flex justify-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#008080]" />
        </div>
      ) : couriers.length === 0 ? (
        <div className="bg-white p-12 text-center rounded-xl border border-gray-200 shadow-sm">
          <p className="text-gray-500">No courier rates configured for this plan.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          
          {/* Tabs */}
          <div className="flex border-b border-gray-200 bg-gray-50">
            {couriers.map((courier: any) => (
              <button
                key={courier.courierId}
                onClick={() => { setActiveTab(courier.courierId); setIsEditing(false); }}
                className={`px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === courier.courierId 
                    ? 'bg-white text-[#008080] border-b-2 border-[#008080]' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                {courier.courierName}
              </button>
            ))}
          </div>

          {activeCourier && (
            <div className="p-6">
              
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">{activeCourier.courierName} Rates</h3>
                  <div className="text-sm text-gray-500 mt-1">By {activeCourier.mode || 'Surface'}</div>
                </div>
                {isEditing ? (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
                    <Button 
                      className="bg-[#008080] hover:bg-teal-700 text-white flex items-center gap-2"
                      onClick={handleSave}
                      disabled={isUpdating}
                    >
                      {isUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save Changes
                    </Button>
                  </div>
                ) : (
                  <Button 
                    className="bg-[#008080] hover:bg-teal-700 text-white flex items-center gap-2"
                    onClick={() => setIsEditing(true)}
                  >
                    <Pencil className="w-4 h-4" />
                    Edit Rates
                  </Button>
                )}
              </div>

              {/* Rate Matrix */}
              <div className="mb-10">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Rate Matrix (Per KG)</h4>
                <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-100 text-gray-600 font-semibold text-xs uppercase tracking-wider sticky top-0">
                      <tr>
                        <th className="px-4 py-3 border-b border-r border-gray-200 bg-gray-100 sticky left-0 z-10 w-32 shadow-[1px_0_0_0_#e5e7eb]">Origin \ Dest</th>
                        {destZones.map(dz => (
                          <th key={dz} className="px-4 py-3 border-b border-gray-200 text-center min-w-[100px]">{dz}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {originZones.map((oz, i) => (
                        <tr key={oz} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-3 border-r border-gray-200 font-medium text-gray-700 sticky left-0 z-10 shadow-[1px_0_0_0_#e5e7eb] bg-inherit">{oz}</td>
                          {destZones.map(dz => {
                            const index = editedMatrix.findIndex(m => m.originZone === oz && m.destinationZone === dz);
                            const cellData = index !== -1 ? editedMatrix[index] : null;
                            
                            return (
                              <td key={`${oz}-${dz}`} className="px-4 py-2 text-center align-middle hover:bg-gray-100 transition-colors">
                                {isEditing ? (
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    className="w-full p-1.5 border border-gray-300 rounded focus:ring-[#008080] focus:border-[#008080] text-center text-sm"
                                    value={cellData?.ratePerKg ?? ''}
                                    onChange={(e) => index !== -1 && handleMatrixChange(index, e.target.value)}
                                  />
                                ) : (
                                  <span className="font-medium text-gray-800">{cellData?.ratePerKg ?? '-'}</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Additional Charges */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-medium text-gray-700">Additional Charges</h4>
                </div>
                
                <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm mb-4">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-100 text-gray-600 font-semibold text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-3 border-b">Code</th>
                        <th className="px-4 py-3 border-b">Label</th>
                        <th className="px-4 py-3 border-b">Type</th>
                        <th className="px-4 py-3 border-b text-right">Value</th>
                        <th className="px-4 py-3 border-b text-right">Min Value</th>
                        <th className="px-4 py-3 border-b">Unit</th>
                        <th className="px-4 py-3 border-b text-center w-24">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {editedCharges.map((charge, i) => (
                        <tr key={charge.id || i} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 text-gray-600 font-mono text-xs">{charge.chargeCode}</td>
                          <td className="px-4 py-3 font-medium text-gray-800">
                            {isEditing ? (
                              <input type="text" className="w-full p-1 border border-gray-300 rounded text-sm" value={charge.chargeLabel} onChange={e => handleChargeChange(i, 'chargeLabel', e.target.value)} />
                            ) : charge.chargeLabel}
                          </td>
                          <td className="px-4 py-3">
                            {isEditing ? (
                              <select className="w-full p-1 border border-gray-300 rounded text-sm" value={charge.chargeType} onChange={e => handleChargeChange(i, 'chargeType', e.target.value)}>
                                <option value="FIXED">FIXED</option>
                                <option value="PERCENTAGE">PERCENTAGE</option>
                              </select>
                            ) : charge.chargeType}
                          </td>
                          <td className="px-4 py-3 text-right font-medium">
                            {isEditing ? (
                              <input type="number" className="w-full p-1 border border-gray-300 rounded text-sm text-right" value={charge.chargeValue ?? ''} onChange={e => handleChargeChange(i, 'chargeValue', e.target.value)} />
                            ) : (charge.chargeType === 'PERCENTAGE' ? `${charge.chargeValue}%` : `₹${charge.chargeValue}`)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {isEditing ? (
                              <input type="number" className="w-full p-1 border border-gray-300 rounded text-sm text-right" value={charge.minValue ?? ''} onChange={e => handleChargeChange(i, 'minValue', e.target.value)} />
                            ) : (charge.minValue ? `₹${charge.minValue}` : '-')}
                          </td>
                          <td className="px-4 py-3 text-gray-500">
                            {isEditing ? (
                              <input type="text" className="w-full p-1 border border-gray-300 rounded text-sm" value={charge.unit || ''} onChange={e => handleChargeChange(i, 'unit', e.target.value)} />
                            ) : charge.unit || '-'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {!isEditing && (
                                <button
                                  type="button"
                                  onClick={() => handleEditChargeClick(charge, i)}
                                  className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1.5 rounded transition-colors"
                                  title="Edit Charge"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  if (isEditing) {
                                    setEditedCharges(editedCharges.filter((_, idx) => idx !== i));
                                  } else {
                                    handleDeleteCharge(i);
                                  }
                                }}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1.5 rounded transition-colors"
                                title="Remove Charge"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {editedCharges.length === 0 && (
                        <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500 text-sm">No additional charges found.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {!showAddCharge ? (
                  <Button variant="outline" size="sm" onClick={() => setShowAddCharge(true)} className="flex items-center gap-1 text-[#008080] border-[#008080] hover:bg-teal-50">
                    <Plus className="w-4 h-4" /> Add Charge
                  </Button>
                ) : (
                  <div className="bg-gray-50 p-4 border border-gray-200 rounded-lg flex items-end gap-3 shadow-inner">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Label *</label>
                      <Input placeholder="e.g. Fuel Surcharge" value={newCharge.label} onChange={e => setNewCharge({...newCharge, label: e.target.value})} className="h-9" />
                    </div>
                    <div className="w-24">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Code</label>
                      <Input placeholder="FSC" value={newCharge.code} onChange={e => setNewCharge({...newCharge, code: e.target.value})} className="h-9 font-mono" />
                    </div>
                    <div className="w-32">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Type *</label>
                      <Select 
                        value={newCharge.type} 
                        onChange={e => setNewCharge({...newCharge, type: e.target.value})} 
                        className="h-9"
                        options={[
                          { label: 'FIXED', value: 'FIXED' },
                          { label: 'PERCENTAGE', value: 'PERCENTAGE' }
                        ]}
                      />
                    </div>
                    <div className="w-24">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Value *</label>
                      <Input type="number" placeholder="0.00" value={newCharge.value} onChange={e => setNewCharge({...newCharge, value: e.target.value})} className="h-9" />
                    </div>
                    <div className="w-24">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Min Value</label>
                      <Input type="number" placeholder="0.00" value={newCharge.min} onChange={e => setNewCharge({...newCharge, min: e.target.value})} className="h-9" />
                    </div>
                    <div className="w-32">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Unit</label>
                      <Input placeholder="per kg" value={newCharge.unit} onChange={e => setNewCharge({...newCharge, unit: e.target.value})} className="h-9" />
                    </div>
                    <div className="flex gap-2">
                      <Button className="bg-[#008080] hover:bg-teal-700 text-white h-9 px-4" onClick={handleAddChargeSubmit} disabled={isAddingCharge}>
                        {isAddingCharge ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                      </Button>
                      <Button variant="ghost" className="h-9 px-3 text-gray-500 hover:text-gray-700" onClick={() => setShowAddCharge(false)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      <Modal />
    </div>
  );
}
